import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useNotify, usePermissions, useTranslate, useLocaleState } from 'react-admin';

// UI
import { Grid, Typography, Box, Container, Button } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PaymentsIcon from '@mui/icons-material/Payments';
import SummarizeIcon from '@mui/icons-material/Summarize';
import StorefrontIcon from '@mui/icons-material/Storefront';
import BusinessIcon from '@mui/icons-material/Business';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

// Libs
import { formatDateValue } from '../utils/dateUtils';

import useSaleReport from '../components/hooks/useSaleReport';
import EmptyState from '../components/sales/EmptyState';
import OrganisatorInformation from '../components/sales/OrganisatorInformation';
import OwnershipSalesSummary from '../components/sales/OwnershipSalesSummary';
import SaleReportAccordionSection, {
  OwnershipLinesTable,
  SECTION_TITLE_SX,
} from '../components/sales/SaleReportAccordionSection';
import SaleReportExportDialog from '../components/sales/SaleReportExportDialog';
import SettlementEntryModal from '../components/sales/SettlementEntryModal';
import SessionTimeAndAvailability from '../components/sales/SessionTimeAndAvailability';
import PaymentStatus from '../components/events/PaymentStatus';
import PaymentModal from '../components/events/PaymentModal';
import EventSessionUrlScopeBar from '../components/events/EventSessionUrlScopeBar';

// Services
import {
  makeEventPayment,
  addEventSettlementEntry,
  deleteEventSettlementEntry,
} from '../services/eventService';
import { fetchEventById } from '../services/salesReportService';
import { db } from '../database/firebase';

// Definitions
import { D_COLUMNS, R_COLUMNS, C_COLUMNS } from '../definitions';

const SaleReportPage = () => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const [searchParams] = useSearchParams();
  const notify = useNotify();
  const { permissions } = usePermissions();
  const isAdmin = permissions?.crole === 'admin';

  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [eventData, setEventData] = useState(false);
  const [isSeatedEvent, setIsSeatedEvent] = useState(false);
  const [stockData, setStockData] = useState(false);

  const [salesDataGrid, setSalesDataGrid] = useState([]);
  const [columns] = useState(D_COLUMNS);

  const [couponsData, setCouponsData] = useState(false);
  const [couponColumns] = useState(C_COLUMNS);

  const [remainingData, setRemainingData] = useState([]);
  const [remainingColumns] = useState(R_COLUMNS);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [settlementOpen, setSettlementOpen] = useState(false);
  const [settlementType, setSettlementType] = useState('income');
  const [exportOpen, setExportOpen] = useState(false);

  const eventID = searchParams.get('eventID') ? searchParams.get('eventID') : '1';
  const stockID = searchParams.get('stockID') ? searchParams.get('stockID') : null;

  const {
    loading,
    error: hookError,
    event,
    setEvent,
    stock,
    stocks,
    salesGrid,
    couponsGrid,
    remainingGrid,
    totals,
    ownershipBreakdown,
    isSeatedEvent: seated,
  } = useSaleReport({ eventId: eventID, stockId: stockID });

  useEffect(() => {
    setError(hookError || null);
    setIsLoading(loading);
    if (event) setEventData(event);
    if (stock) setStockData(stock);
    setIsSeatedEvent(seated);
    if (salesGrid) setSalesDataGrid(salesGrid);
    if (couponsGrid) setCouponsData(couponsGrid);
    if (remainingGrid) setRemainingData(remainingGrid);
  }, [hookError, loading, event, stock, seated, salesGrid, couponsGrid, remainingGrid]);

  const handleSubmitPayment = async (payment) => {
    try {
      await makeEventPayment(eventData.id, payment);
      const refreshed = await fetchEventById(db, eventData.id);
      if (refreshed) {
        setEventData(refreshed);
        setEvent(refreshed);
      }
      notify('Ödeme kaydedildi.', { type: 'success' });
    } catch (err) {
      notify('Ödeme kaydedilirken hata oluştu, lütfen Gişe Kıbrıs ile iletişime geçin.', {
        type: 'error',
      });
      throw err;
    }
  };

  const handleSettlementSubmit = async ({
    type,
    amount,
    note,
    categoryId,
    categoryName,
    categoryCode,
    documents,
  }) => {
    try {
      await addEventSettlementEntry(
        eventData.id,
        {
          type,
          amount,
          note,
          categoryId,
          categoryName,
          categoryCode,
          documents,
          createdBy: permissions?.email || null,
        },
        eventData.settlementEntries,
      );
      const refreshed = await fetchEventById(db, eventData.id);
      if (refreshed) {
        setEventData(refreshed);
        setEvent(refreshed);
      }
      notify(type === 'expense' ? 'Gider (kullanıldı) eklendi.' : 'Gelir (alındı) eklendi.', {
        type: 'success',
      });
    } catch (err) {
      notify('Kayıt eklenirken hata oluştu.', { type: 'error' });
      throw err;
    }
  };

  const handleDeleteSettlement = async (entry) => {
    try {
      await deleteEventSettlementEntry(eventData.id, entry.id, eventData.settlementEntries);
      const refreshed = await fetchEventById(db, eventData.id);
      if (refreshed) {
        setEventData(refreshed);
        setEvent(refreshed);
      }
      notify('Düzeltme silindi.', { type: 'success' });
    } catch (err) {
      notify('Düzeltme silinirken hata oluştu.', { type: 'error' });
      throw err;
    }
  };

  const eventStatsData = useMemo(
    () => ({
      totalRevenue: totals.totalProductSales || 0,
      totalCouponDiscount: totals.totalCouponDiscount || 0,
      totalRemaining: totals.totalRemainingAmount || 0,
      totalAmountOfCashSales: totals.orgVenueSales || 0,
      totalCommssionAmount: totals.totalCommissionAmount || 0,
    }),
    [totals],
  );

  const salesTotalRow = useMemo(
    () => (Array.isArray(salesDataGrid) ? salesDataGrid.find((r) => r.id === 'total') : null),
    [salesDataGrid],
  );
  const couponsTotalRow = useMemo(
    () => (Array.isArray(couponsData) ? couponsData.find((r) => r.id === 'total') : null),
    [couponsData],
  );
  const remainingTotalRow = useMemo(
    () => (Array.isArray(remainingData) ? remainingData.find((r) => r.id === 'total') : null),
    [remainingData],
  );

  const exportDialog = (
    <SaleReportExportDialog
      open={exportOpen}
      onClose={() => setExportOpen(false)}
      event={eventData}
      stock={stockData}
      salesGrid={salesDataGrid}
      couponsGrid={Array.isArray(couponsData) ? couponsData : []}
      remainingGrid={remainingData}
      ownershipBreakdown={ownershipBreakdown}
      totals={totals}
      onNotify={(msg, opts) => notify(msg, opts)}
    />
  );

  if (!eventID)
    return (
      <EmptyState
        title={translate('ra.notification.item_does_not_exist', { _: 'Hata' })}
        message={translate('report.event_id_missing', { _: 'Etkinlik ID eksik' })}
        isError={true}
      />
    );

  const sessionScopeBar =
    eventID && eventID !== '1' ? (
      <Container maxWidth="lg" sx={{ mt: 2 }}>
        <EventSessionUrlScopeBar
          eventID={eventID}
          preloadedStocks={stocks}
          preloadedDateType={event?.dateType ?? null}
          skipNetwork
        />
      </Container>
    ) : null;

  if (error)
    return (
      <>
        {sessionScopeBar}
        <EmptyState
          title={translate('ra.notification.item_does_not_exist', { _: 'Hata' })}
          message={error}
          isError={true}
        />
      </>
    );
  if (isLoading || !eventData)
    return (
      <>
        {sessionScopeBar}
        <EmptyState
          title={translate('ra.page.loading', { _: 'Yükleniyor...' })}
          message={translate('ra.message.loading', { _: 'Lütfen bekleyin...' })}
          isError={false}
        />
      </>
    );

  if (isSeatedEvent)
    return (
      eventData &&
      eventData.sessions && (
        <Box style={{ marginBottom: 32 }}>
          {sessionScopeBar}
          <Container maxWidth="lg">
            <Box
              style={{
                paddingTop: 8,
                paddingBottom: 8,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 16,
                flexWrap: 'wrap',
              }}
            >
              <Box>
                <Typography variant="h4" gutterBottom>
                  {translate('report.title', { _: 'Satış Raporu' })} / {eventData.name}
                </Typography>
                <Typography variant="subtitle1">
                  {stockData?.date
                    ? formatDateValue(stockData.date, 'LL', locale)
                    : formatDateValue(eventData.startdate, 'LL', locale)}{' '}
                  {stockData.time ? stockData.time : ''}
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<FileDownloadIcon />}
                onClick={() => setExportOpen(true)}
              >
                {translate('report.export_report', { _: 'Rapor Al' })}
              </Button>
            </Box>
          </Container>
          <OrganisatorInformation event={eventData} />
          <SessionTimeAndAvailability event={eventData} stock={stockData} />
          <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
            <Grid container spacing={1}>
              <Grid item xs={12} sx={{ mb: 2 }}>
                <Grid container>
                  <Grid item xs={12}>
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}
                    >
                      <SummarizeIcon fontSize="small" />
                      <Typography variant="h6" sx={SECTION_TITLE_SX}>
                        {translate('report.general', { _: 'Genel' })}
                      </Typography>
                    </div>
                    <OwnershipSalesSummary
                      event={eventData}
                      totals={totals}
                      isAdmin={isAdmin}
                      onAddExpense={() => {
                        setSettlementType('expense');
                        setSettlementOpen(true);
                      }}
                      onAddIncome={() => {
                        setSettlementType('income');
                        setSettlementOpen(true);
                      }}
                      onPay={() => setPaymentOpen(true)}
                      onDeleteSettlement={isAdmin ? handleDeleteSettlement : undefined}
                      paymentStatusSlot={
                        <PaymentStatus
                          event={eventData}
                          eventStatsData={eventStatsData}
                          isAdmin={false}
                          inline
                        />
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>
              {(ownershipBreakdown?.owners || []).map((owner) => (
                <Grid item xs={12} key={`${owner.type}-${owner.id}`}>
                  <SaleReportAccordionSection
                    title={
                      owner.type === 'venue'
                        ? `${translate('report.venue_prefix', { _: 'Mekan' })}: ${owner.name}`
                        : `${translate('report.organisation_prefix', { _: 'Organizasyon Şirketi' })}: ${owner.name}`
                    }
                    icon={
                      owner.type === 'venue' ? (
                        <StorefrontIcon fontSize="small" />
                      ) : (
                        <BusinessIcon fontSize="small" />
                      )
                    }
                    totalLabel={translate('report.total', { _: 'Toplam' })}
                    totalValue={owner.total || 0}
                    details={
                      <OwnershipLinesTable lines={owner.lines || []} total={owner.total || 0} />
                    }
                    defaultExpanded={false}
                  />
                </Grid>
              ))}
              <Grid item xs={12}>
                <SaleReportAccordionSection
                  title="Gişe Kıbrıs"
                  icon={<ConfirmationNumberIcon fontSize="small" />}
                  totalLabel={translate('report.total', { _: 'Toplam' })}
                  totalValue={ownershipBreakdown?.gise?.total || 0}
                  details={
                    <OwnershipLinesTable
                      lines={ownershipBreakdown?.gise?.lines || []}
                      total={ownershipBreakdown?.gise?.total || 0}
                    />
                  }
                  defaultExpanded={false}
                />
              </Grid>
            </Grid>
          </Container>
          {exportDialog}
          {isAdmin && (
            <>
              <PaymentModal
                open={paymentOpen}
                onClose={() => setPaymentOpen(false)}
                event={eventData}
                eventStatsData={eventStatsData}
                onSubmit={handleSubmitPayment}
              />
              <SettlementEntryModal
                open={settlementOpen}
                onClose={() => setSettlementOpen(false)}
                entryType={settlementType}
                onSubmit={handleSettlementSubmit}
              />
            </>
          )}
        </Box>
      )
    );

  return (
    <Box style={{ flex: 1 }}>
      {sessionScopeBar}
      <Container maxWidth="lg">
        <Box
          style={{
            paddingTop: 8,
            paddingBottom: 8,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 16,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ flex: 1, minWidth: 200 }}>
            {translate('report.title', { _: 'Satış Raporu' })} / {eventData.name} -{' '}
            {stockData?.date
              ? formatDateValue(stockData.date, 'LL', locale)
              : formatDateValue(eventData.startdate, 'LL', locale)}{' '}
            / {stockData.time ? stockData.time : ''}
          </Typography>
          <Button
            variant="contained"
            startIcon={<FileDownloadIcon />}
            onClick={() => setExportOpen(true)}
            sx={{ flexShrink: 0 }}
          >
            {translate('report.export_report', { _: 'Rapor Al' })}
          </Button>
        </Box>
      </Container>

      <OrganisatorInformation event={eventData} />

      <Container maxWidth="lg" sx={{ mb: 4 }}>
        <Grid container spacing={1}>
          <Grid item xs={12} sx={{ mb: 2 }}>
            <Grid container>
              <Grid item xs={12}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <SummarizeIcon fontSize="small" />
                  <Typography variant="h6" sx={SECTION_TITLE_SX}>
                    {translate('report.general', { _: 'Genel' })}
                  </Typography>
                </div>
                <OwnershipSalesSummary
                  event={eventData}
                  totals={totals}
                  isAdmin={isAdmin}
                  onAddExpense={() => {
                    setSettlementType('expense');
                    setSettlementOpen(true);
                  }}
                  onAddIncome={() => {
                    setSettlementType('income');
                    setSettlementOpen(true);
                  }}
                  onPay={() => setPaymentOpen(true)}
                  onDeleteSettlement={isAdmin ? handleDeleteSettlement : undefined}
                  paymentStatusSlot={
                    <PaymentStatus
                      event={eventData}
                      eventStatsData={eventStatsData}
                      isAdmin={false}
                      inline
                    />
                  }
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <SaleReportAccordionSection
              title={translate('report.sales', { _: 'Satışlar' })}
              icon={<TrendingUpIcon fontSize="small" />}
              totalLabel={translate('report.total_sale', { _: 'Toplam Satış' })}
              totalValue={salesTotalRow?.totalSale ?? totals.totalProductSales ?? 0}
              rows={salesDataGrid}
              columns={columns}
              defaultExpanded={false}
            />
          </Grid>

          <Grid item xs={12}>
            <SaleReportAccordionSection
              title={translate('report.coupons', { _: 'Kuponlar' })}
              icon={<LocalOfferIcon fontSize="small" />}
              totalLabel={translate('report.total_discount', { _: 'Toplam İndirim' })}
              totalValue={couponsTotalRow?.totalDiscount ?? totals.totalCouponDiscount ?? 0}
              rows={Array.isArray(couponsData) ? couponsData : []}
              columns={couponColumns}
              defaultExpanded={false}
            />
          </Grid>

          <Grid item xs={12}>
            <SaleReportAccordionSection
              title={translate('report.remaining_payments', { _: 'Kalan Ödemeler' })}
              icon={<PaymentsIcon fontSize="small" />}
              totalLabel={translate('report.total_remaining_amount', { _: 'Toplam Kalan Tutar' })}
              totalValue={
                remainingTotalRow?.totalRmainingAmount ?? totals.totalRemainingAmount ?? 0
              }
              rows={remainingData}
              columns={remainingColumns}
              defaultExpanded={false}
            />
          </Grid>

          {(ownershipBreakdown?.owners || []).map((owner) => (
            <Grid item xs={12} key={`${owner.type}-${owner.id}`}>
              <SaleReportAccordionSection
                title={
                  owner.type === 'venue'
                    ? `${translate('report.venue_prefix', { _: 'Mekan' })}: ${owner.name}`
                    : `${translate('report.organisation_prefix', { _: 'Organizasyon Şirketi' })}: ${owner.name}`
                }
                icon={
                  owner.type === 'venue' ? (
                    <StorefrontIcon fontSize="small" />
                  ) : (
                    <BusinessIcon fontSize="small" />
                  )
                }
                totalLabel={translate('report.total', { _: 'Toplam' })}
                totalValue={owner.total || 0}
                details={<OwnershipLinesTable lines={owner.lines || []} total={owner.total || 0} />}
                defaultExpanded={false}
              />
            </Grid>
          ))}

          <Grid item xs={12}>
            <SaleReportAccordionSection
              title="Gişe Kıbrıs"
              icon={<ConfirmationNumberIcon fontSize="small" />}
              totalLabel={translate('report.total', { _: 'Toplam' })}
              totalValue={ownershipBreakdown?.gise?.total || 0}
              details={
                <OwnershipLinesTable
                  lines={ownershipBreakdown?.gise?.lines || []}
                  total={ownershipBreakdown?.gise?.total || 0}
                />
              }
              defaultExpanded={false}
            />
          </Grid>
        </Grid>
      </Container>

      {exportDialog}

      {isAdmin && (
        <>
          <PaymentModal
            open={paymentOpen}
            onClose={() => setPaymentOpen(false)}
            event={eventData}
            eventStatsData={eventStatsData}
            onSubmit={handleSubmitPayment}
          />
          <SettlementEntryModal
            open={settlementOpen}
            onClose={() => setSettlementOpen(false)}
            entryType={settlementType}
            onSubmit={handleSettlementSubmit}
          />
        </>
      )}
    </Box>
  );
};

export default SaleReportPage;
